const MAX_WIDTH_PX = 1600;
const MAX_HEIGHT_PX = 1600;
const SKIP_BELOW_BYTES = 200 * 1024;
const JPEG_QUALITY = 0.82;

const isDownscalable = (file: File): boolean =>
  file.type.startsWith('image/') && file.type !== 'image/gif' && file.type !== 'image/svg+xml';

export const downscaleImage = async (file: File): Promise<File> => {
  if (!isDownscalable(file) || file.size <= SKIP_BELOW_BYTES) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }

  const scale = Math.min(MAX_WIDTH_PX / bitmap.width, MAX_HEIGHT_PX / bitmap.height, 1);
  if (scale === 1) {
    bitmap.close();
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return file;
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const targetType = file.type === 'image/png' ? 'image/jpeg' : file.type;
  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, targetType, JPEG_QUALITY),
  );
  if (!blob || blob.size >= file.size) {
    return file;
  }

  const targetName =
    targetType === file.type ? file.name : file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return new File([blob], targetName, { lastModified: file.lastModified, type: targetType });
};
